import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Airflow } from '@aviata/platform-types';
import type { ticketDetails } from 'shared';

import * as featureDiscountLib from '../lib';
import ru from '../config/locales/ru.json';
import kk from '../config/locales/kk.json';
import en from '../config/locales/en.json';

const FEATURES = {
  cheapest: 'cheapest',
  fastest: 'fastest',
  cheapestFastest: 'cheapest-fastest',
  discount: 'discount',
  recommended: 'recommended',
  freeText: 'free-text',
};

export const useLabels = (
  features: Partial<Airflow.Offer.Features>,
  isRecommendedSortingMode: boolean,
  rankingTags?: string[],
  transportKind?: ticketDetails.lib.OfferTransportKind,
) => {
  const { t } = useI18n({ messages: { ru, kk, en } });

  const FEATURES_DATA = {
    [FEATURES.cheapest]: {
      text: t('offerCardLabels.cheapest'),
      variant: 'orange',
      icon: 'cheapest-icon',
    },
    [FEATURES.fastest]: {
      text: t('offerCardLabels.fastest'),
      variant: 'green',
      icon: 'fastest-icon',
    },
    [FEATURES.cheapestFastest]: {
      text: t('offerCardLabels.cheapestFastest'),
      variant: 'blue',
      icon: 'cheapest-and-fastest-icon',
    },
    [FEATURES.discount]: {
      text: t('offerCardLabels.discount'),
      variant: 'red',
    },
    [FEATURES.recommended]: {
      text: t('offerCardLabels.recommended'),
      variant: 'skyblue',
      icon: 'recommended-icon',
    },
  } as const;

  // Priority
  // 1.Aviata labels
  // 2.discount
  // 3.recommended

  const labels = computed(() => {
    const result = [];

    if (transportKind === 'mixed') {
      result.push({
        text: t('offerCardLabels.multimodal'),
        textColor: '#FFFFFF',
        backgroundColor: '#1D6BC0',
      });
    } else if (transportKind === 'rail') {
      result.push({
        text: t('offerCardLabels.railOnly'),
        textColor: '#FFFFFF',
        backgroundColor: '#1D6BC0',
      });
    }

    if (features.freetext) {
      result.push({
        text: features.freetext.text.toUpperCase(),
        textColor: features.freetext.text_color,
        backgroundColor: features.freetext.background_color,
      });
    }

    if (isRecommendedSortingMode) {
      if (rankingTags !== undefined) {
        const tagSet = new Set(rankingTags);
        const hasCheapestTag = tagSet.has(FEATURES.cheapest);
        const hasFastestTag = tagSet.has(FEATURES.fastest);

        if (hasCheapestTag && hasFastestTag) {
          result.push(FEATURES_DATA[FEATURES.cheapestFastest]);
        } else {
          if (hasCheapestTag) {
            result.push(FEATURES_DATA[FEATURES.cheapest]);
          }
          if (hasFastestTag) {
            result.push(FEATURES_DATA[FEATURES.fastest]);
          }
        }
      } else if (features.cheapest?.value && features.fastest?.value) {
        result.push(FEATURES_DATA[FEATURES.cheapestFastest]);
      } else {
        if (features.cheapest?.value) {
          result.push(FEATURES_DATA[FEATURES.cheapest]);
        }

        if (features.fastest?.value) {
          result.push(FEATURES_DATA[FEATURES.fastest]);
        }
      }
    }

    if (features.discount) {
      if (
        typeof features.discount === 'object' &&
        featureDiscountLib.getDiscountText
      ) {
        result.push({
          text: featureDiscountLib.getDiscountText(features.discount),
          variant: FEATURES_DATA[FEATURES.discount].variant,
        });
      }
    }

    if (features.recommended?.value) {
      result.push(FEATURES_DATA[FEATURES.recommended]);
    }

    return result;
  });

  return labels;
};
